import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProductForm, type ProductFormSubmitValues } from "@/components/ui/ProductForm";
import { PENDING_IMAGE_PLACEHOLDER } from "@/components/ui/VariantFields";
import { apiFetch, setProductStatus, uploadVariantImage } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { queryClient } from "@/lib/queryClient";
import { clearProductOrigin } from "@/lib/productNavigation";

export default function CreateProduct() {
  const { t } = useTranslation();
  usePageTitle(t("product.newProduct"));
  const navigate = useNavigate();

  const onSubmit = async (data: ProductFormSubmitValues) => {
    const { variants, productId } = data;

    if (!productId) throw new Error(t("product.couldNotCreateProduct"));

    if (variants && variants.length) {
      const results = await Promise.all(
        variants.map(async ({ payload, imageFile, announcement_id, is_unavailable }) => {
          // The variant row must be created (and must succeed) before we
          // ever try to save a picture for it - never the other way round.
          const res = await apiFetch("/variant", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...payload,
              img_url: PENDING_IMAGE_PLACEHOLDER,
              product_id: productId,
              announcement_id,
              is_unavailable,
            }),
          });
          if (!res.ok) return false;

          if (imageFile) {
            const created = await res.json();
            // A failed image upload doesn't roll back the variant - the
            // product just falls back to the placeholder image until
            // someone retries the upload from the edit page.
            await uploadVariantImage(created.id, imageFile).catch(() => {});
          }
          return true;
        })
      );
      if (results.some(ok => !ok)) {
        throw new Error(t("product.savedAsDraftSomeVariantsFailed"));
      }
    }

    let product;
    try {
      product = await setProductStatus(productId, "published");
    } catch (err) {
      const message = err instanceof Error ? err.message : t("product.couldNotSaveProduct");
      throw new Error(t("product.savedButPublishFailed", { reason: `${message.charAt(0).toLowerCase()}${message.slice(1)}` }));
    }

    clearProductOrigin();
    await queryClient.invalidateQueries({ queryKey: ["products", "announced"] });

    navigate(`/product/${product.slug}/p/${product.id}`);
  };

  return (
    <div className="flex flex-col items-center gap-4 pb-10">
      <h1 className="text-2xl font-bold text-brand">{t("product.newProduct")}</h1>
      <ProductForm mode="create" submitLabel={t("product.create")} pendingLabel={t("product.creating")} onSubmit={onSubmit} />
    </div>
  );
}
